import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Check, Loader2, Sparkles, Users, Trees, BarChart3, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CURRENCY = { gbp: "£", usd: "$", eur: "€" };

const PERKS = [
  { icon: Check, text: "Unlimited habits — check-off & countable, with streaks" },
  { icon: Sparkles, text: "AI Coach & AI Plan Builder for any goal" },
  { icon: Trees, text: "Momentum Garden, XP, levels & achievements" },
  { icon: Users, text: "Squad leaderboards & group challenges" },
  { icon: BarChart3, text: "Full analytics, heatmaps & daily check-ins" },
];

export default function Upgrade() {
  const { user, logout } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/plan").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start checkout");
      setLoading(false);
    }
  };

  const symbol = plan ? (CURRENCY[plan.currency] || "£") : "£";
  const price = plan ? (plan.amount / 100).toFixed(2) : "9.99";

  return (
    <div className="min-h-screen grain flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
        data-testid="upgrade-page"
      >
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo.png" alt="SMART Momentum" className="w-11 h-11 rounded-xl glow-emerald" />
          <span className="font-display text-2xl font-extrabold tracking-tight">
            <span className="text-emerald-400">SMART</span> Momentum
          </span>
        </div>

        <div className="rounded-3xl border border-emerald-500/30 bg-[#12141C] p-7 shadow-2xl">
          <div className="text-center mb-6">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">Momentum Pro</span>
            <h1 className="font-display text-3xl font-black tracking-tight mb-1">
              Your free trial has ended
            </h1>
            <p className="text-white">Subscribe to keep going — and keep everything you've built, {user?.name?.split(" ")[0] || "friend"}.</p>
          </div>

          <div className="text-center mb-7">
            <div className="flex items-end justify-center gap-1">
              <span className="font-display text-5xl font-black">{symbol}{price}</span>
              <span className="text-white mb-2">/ year</span>
            </div>
            <p className="text-xs text-white mt-1">Billed yearly · cancel anytime</p>
          </div>

          <ul className="space-y-3 mb-8">
            {PERKS.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <p.icon className="w-3.5 h-3.5 text-emerald-400" />
                </span>
                <span className="text-white">{p.text}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="subscribe-button"
            onClick={subscribe}
            disabled={loading}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            Subscribe {symbol}{price}/yr
          </button>
          <p className="text-center text-xs text-white mt-3">Secure checkout by Stripe · test card 4242 4242 4242 4242</p>
        </div>

        <button
          data-testid="upgrade-logout"
          onClick={logout}
          className="w-full mt-4 h-11 rounded-xl border border-[#33331f] text-white font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </motion.div>
    </div>
  );
}
